import { useState } from "react";
import { api, API } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Upload, Loader2, X, Send, ShieldCheck, RotateCcw, DollarSign } from "lucide-react";
import { toast } from "sonner";

export const STATUS_STYLE = {
  open: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  under_review: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  resolved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  rejected: "bg-slate-700 text-slate-300 border-slate-600",
};

export function StatusBadge({ status }) {
  return <Badge data-testid={`status-badge-${status}`} className={`border text-[10px] uppercase tracking-wide ${STATUS_STYLE[status] || STATUS_STYLE.open}`}>{status.replace(/_/g, " ")}</Badge>;
}

function Photos({ paths }) {
  if (!paths?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {paths.map((p) => (
        <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-lg overflow-hidden border border-slate-700 bg-slate-900">
          <img src={`${API}/files/${p}`} alt="" className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
}

const ROLE_STYLE = { admin: "text-cyan-300", owner: "text-blue-300", renter: "text-emerald-300" };
const KIND_ICON = { status: ShieldCheck, refund: DollarSign };

export default function DisputeThread({ dispute, onUpdated }) {
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const closed = ["resolved", "rejected"].includes(dispute.status);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const out = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/uploads?kind=doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data.path);
      }
      setPhotos((p) => [...p, ...out]);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/disputes/${dispute.dispute_id}/replies`, { text, photo_paths: photos });
      setText(""); setPhotos([]);
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`dispute-thread-${dispute.dispute_id}`}>
      <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-mono text-slate-400">{dispute.opened_by_name} · {dispute.opened_by_role} · {new Date(dispute.created_at).toLocaleString()}</p>
          <StatusBadge status={dispute.status} />
        </div>
        <p className="text-sm mt-2 font-semibold capitalize">{dispute.kind.replace(/_/g, " ")}</p>
        <p className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{dispute.description}</p>
        <Photos paths={dispute.photo_paths} />
      </div>

      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
        {(dispute.events || []).map((ev) => {
          const Icon = KIND_ICON[ev.kind];
          return (
            <div key={ev.event_id} data-testid={`dispute-event-${ev.event_id}`}
              className={`p-3 rounded-lg border ${ev.kind === "reply" ? "bg-[#0f131c] border-slate-800" : "bg-cyan-500/5 border-cyan-500/20"}`}>
              <div className="flex items-center gap-2 text-xs">
                {Icon && <Icon className="w-3.5 h-3.5 text-cyan-400" />}
                <span className={`font-semibold ${ROLE_STYLE[ev.author_role] || "text-slate-300"}`}>{ev.author_name}</span>
                <span className="text-slate-500 uppercase text-[10px]">{ev.author_role}</span>
                <span className="text-slate-600 ml-auto font-mono">{new Date(ev.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{ev.text}</p>
              {ev.kind === "status" && <StatusBadge status={ev.status} />}
              <Photos paths={ev.photo_paths} />
            </div>
          );
        })}
        {(dispute.events || []).length === 0 && <p className="text-xs text-slate-500 text-center py-2">No replies yet.</p>}
      </div>

      {closed && dispute.opened_by_role !== "admin" ? (
        <p className="text-xs text-slate-500 text-center p-3 rounded-lg border border-dashed border-slate-800" data-testid="dispute-closed-note">
          <RotateCcw className="inline w-3 h-3 mr-1" />This ticket is {dispute.status}. Contact support to reopen.
        </p>
      ) : null}
      <form onSubmit={send} className="space-y-2" data-testid="dispute-reply-form">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="dispute-reply-text" disabled={closed && !dispute._admin}
          placeholder={closed ? "Ticket closed" : "Add a reply or more details…"} className="bg-slate-900 border-slate-800 min-h-[70px]" />
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <span key={p} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-xs font-mono">evidence-{i + 1}
                <button type="button" onClick={() => setPhotos((ps) => ps.filter((x) => x !== p))}><X className="w-3 h-3" /></button></span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className={`flex items-center gap-1.5 h-9 px-3 rounded-lg border border-dashed border-slate-700 text-xs cursor-pointer hover:border-slate-500 ${closed && !dispute._admin ? "opacity-40 pointer-events-none" : ""}`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Attach evidence
            <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="dispute-reply-photos" />
          </label>
          <Button type="submit" size="sm" disabled={busy || !text.trim() || (closed && !dispute._admin)} data-testid="dispute-reply-send" className="ml-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Reply</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
